const TermsOfService = () => {
  return (
    <div className="bg-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Terms of Service</h1>
          <p className="text-xl text-primary-100">
            Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="prose prose-lg max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-700 mb-4">
              By accessing and using VigilantEdge's website and services, you accept and agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Description of Services</h2>
            <p className="text-gray-700 mb-4">
              VigilantEdge provides professional security assessment services, including but not limited to:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>External Network Penetration Testing</li>
              <li>External Web Application Penetration Testing</li>
              <li>External OSINT Assessments</li>
              <li>Internal Network Security Controls Assessments</li>
              <li>Internal Vulnerability Assessments</li>
            </ul>
            <p className="text-gray-700 mb-4">
              We reserve the right to modify or discontinue any service at any time without notice.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. User Accounts</h2>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Registration</h3>
            <p className="text-gray-700 mb-4">
              To access certain features, you must register for an account. You agree to:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain and update your information</li>
              <li>Keep your password secure and confidential</li>
              <li>Notify us immediately of any unauthorized access</li>
              <li>Accept responsibility for all activities under your account</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Account Approval</h3>
            <p className="text-gray-700 mb-4">
              All new accounts are subject to approval. We reserve the right to refuse service to anyone for any reason at any time.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. SMS Communications Terms</h2>
            <p className="text-gray-700 mb-4">
              By providing your mobile phone number and opting in to receive SMS messages, you agree to the following:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>You consent to receive automated text messages from VigilantEdge at the phone number provided</li>
              <li>Messages may include appointment reminders, service updates, security notifications, and account alerts</li>
              <li>Message frequency varies based on your account activity and scheduled services</li>
              <li>Standard message and data rates may apply from your mobile carrier</li>
              <li>VigilantEdge does not charge for SMS messages, but your carrier may</li>
              <li>You can opt out at any time by texting STOP to any message</li>
              <li>After texting STOP, you will receive one final confirmation message</li>
              <li>For assistance, text HELP or email info@vigilantedge.tech</li>
              <li>Carriers are not liable for delayed or undelivered messages</li>
            </ul>
            <p className="text-gray-700 mb-4">
              <strong>Supported Carriers:</strong> AT&T, T-Mobile, Verizon, Sprint, and most major U.S. carriers.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Acceptable Use Policy</h2>
            <p className="text-gray-700 mb-4">
              You agree not to:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>Use our services for any illegal purpose</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with or disrupt our services</li>
              <li>Share your account credentials with others</li>
              <li>Upload malicious code or viruses</li>
              <li>Collect information about other users</li>
              <li>Misrepresent your identity or affiliation</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Service Engagement and Scope</h2>
            <p className="text-gray-700 mb-4">
              All security assessment services are provided under a separate Statement of Work (SOW) that defines:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>Scope of testing</li>
              <li>Timeline and deliverables</li>
              <li>Rules of engagement</li>
              <li>Authorized target systems</li>
              <li>Reporting requirements</li>
            </ul>
            <p className="text-gray-700 mb-4">
              Testing will only be performed on systems explicitly authorized in the SOW.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Confidentiality</h2>
            <p className="text-gray-700 mb-4">
              We maintain strict confidentiality of all client information and assessment findings. All security vulnerabilities and assessment results will only be disclosed to authorized personnel within your organization.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Intellectual Property</h2>
            <p className="text-gray-700 mb-4">
              All content on our website, including text, graphics, logos, and software, is the property of VigilantEdge and protected by intellectual property laws. Assessment reports and deliverables become the property of the client upon payment.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Limitation of Liability</h2>
            <p className="text-gray-700 mb-4">
              To the maximum extent permitted by law:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>VigilantEdge shall not be liable for any indirect, incidental, special, or consequential damages</li>
              <li>Our total liability shall not exceed the amount paid for the specific service</li>
              <li>We provide services on an "as is" basis without warranties</li>
              <li>Security assessments are point-in-time evaluations and do not guarantee complete security</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Indemnification</h2>
            <p className="text-gray-700 mb-4">
              You agree to indemnify and hold VigilantEdge harmless from any claims, damages, or expenses arising from your use of our services or violation of these terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Payment Terms</h2>
            <p className="text-gray-700 mb-4">
              Payment terms are specified in the Statement of Work. Generally:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>Payment is due within 30 days of invoice date</li>
              <li>Late payments may incur additional fees</li>
              <li>Services may be suspended for non-payment</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Termination</h2>
            <p className="text-gray-700 mb-4">
              We reserve the right to terminate or suspend your account at any time for violation of these terms. You may terminate your account by contacting us at info@vigilantedge.tech.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Governing Law</h2>
            <p className="text-gray-700 mb-4">
              These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to conflict of law provisions.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Changes to Terms</h2>
            <p className="text-gray-700 mb-4">
              We reserve the right to modify these Terms at any time. Continued use of our services after changes constitutes acceptance of the modified terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">15. Contact Information</h2>
            <p className="text-gray-700 mb-4">
              For questions about these Terms of Service, contact us:
            </p>
            <ul className="list-none mb-4 text-gray-700 space-y-2">
              <li><strong>Email:</strong> info@vigilantedge.tech</li>
              <li><strong>Website:</strong> vigilantedge.tech</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">16. Severability</h2>
            <p className="text-gray-700 mb-4">
              If any provision of these Terms is found to be unenforceable, the remaining provisions will remain in full force and effect.
            </p>
          </section>
        </div>
      </div>
    </div>
  )
}

export default TermsOfService
